<template>
    <page-loader v-if="loadingStatus == 'loading'" />
    <div v-else class="col-sm-12">
        <div class="container rv-page">
            <header class="rv-head">
                <h2 class="rv-head__title">Recent Ancestry versions</h2>
                <p class="rv-head__lead">
                    As our reference panels grow, we update how your Recent Ancestry is worked out. Here's what each
                    version changed, and what's coming next.
                </p>
            </header>

            <!-- The roadmap: what's in progress, then every version, newest first -->
            <section class="rv-roadmap" aria-label="Version history">
                <ol class="rv-timeline">
                    <li v-for="item in timeline" :key="item.version" class="rv-step" :class="`rv-step--${item.state}`">
                        <span class="rv-step__node">{{ item.version }}</span>
                        <article class="rv-step__card">
                            <span v-if="item.state === 'upcoming'" class="rv-chip rv-chip--upcoming">In progress</span>
                            <span v-else-if="item.state === 'current'" class="rv-chip rv-chip--current">Current</span>
                            <h3 class="rv-step__title">{{ item.heading }}</h3>
                            <p v-if="item.sub_heading" class="rv-step__sub">{{ item.sub_heading }}</p>
                            <div class="rv-step__text" v-html="item.text"></div>
                        </article>
                    </li>
                </ol>
            </section>

            <!-- What panels and updates are, from the same content as before without its pictures -->
            <section v-if="explainers.length" class="rv-about">
                <h2 class="rv-about__heading">About panels and updates</h2>
                <article v-for="explainer in explainers" :key="explainer.heading" class="rv-about__card">
                    <h3 class="rv-about__title">{{ explainer.heading }}</h3>
                    <div class="rv-about__text" v-html="explainer.text"></div>
                </article>
            </section>
        </div>
    </div>
</template>

<script>
import '../../../styles/pages/ancentry/recent-ancetry.scss'
import { ref, computed, onMounted } from "vue";
import { getRecentVersions } from "@/services/ancestry";

// Versions on their way, which the CMS doesn't list yet. Each shows until the CMS lists its version.
const UPCOMING = [
    {
        version: "2.2.0",
        heading: "Irish Panel Refinement",
        sub_heading: "Expected Q1 2027",
        text: "<p>Adjustments to the British and Irish panel, allowing for Irish ancestry to be broken down into subregional clusters.</p>",
    },
];

export default {
    name: 'recent-ancestry-version',
    setup() {
        const loadingStatus = ref("loading");
        const versions = ref({});
        onMounted(async () => {
            versions.value = (await getRecentVersions().catch(() => null)) ?? {};
            loadingStatus.value = "loaded";
        });

        // Newest first, as the CMS lists them: the first is the version results are on now
        const timeline = computed(() => {
            const released = (versions.value.major_version_history ?? []).filter((item) => item?.version);
            const listed = new Set(released.map((item) => item.version));
            return [
                ...UPCOMING.filter((item) => !listed.has(item.version)).map((item) => ({ ...item, state: "upcoming" })),
                ...released.map((item, index) => ({ ...item, state: index === 0 ? "current" : "past" })),
            ];
        });

        const explainers = computed(() =>
            (versions.value.panel_update_versions?.[0]?.panel_update_versions ?? []).filter((item) => item?.heading)
        );

        return { loadingStatus, timeline, explainers };
    }
};
</script>

<style lang="scss" scoped>
.rv-page {
    max-width: 880px;
    text-align: left;
    padding-top: 8px;
    padding-bottom: 48px;
}

.rv-head {
    margin-bottom: 28px;
}

.rv-head__title {
    margin: 0 0 8px;
    font-size: 26px;
    font-weight: 700;
    color: #1A1F36;
}

.rv-head__lead {
    margin: 0;
    max-width: 640px;
    font-size: 16px;
    line-height: 1.6;
    color: #4A5472;
}

.rv-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
}

// A node per version with its card beside it, joined to the next version's node by the rail
.rv-step {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 20px;

    & + & { margin-top: 20px; }

    &:not(:last-child)::before {
        content: "";
        position: absolute;
        top: 32px;
        bottom: -20px;
        left: 31px;
        width: 2px;
        background: #DCE1EC;
    }
}

.rv-step__node {
    position: relative;
    z-index: 1;
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 700;
    background: #EEF1F7;
    color: #4A5472;
    border: 2px solid #F7F8FB;
}

.rv-step--current .rv-step__node {
    background: #154096;
    color: #fff;
    box-shadow: 0 0 0 6px rgba(21, 64, 150, 0.12);
}

.rv-step--upcoming .rv-step__node {
    background: #fff;
    color: #154096;
    border: 2px dashed #154096;
}

.rv-step__card {
    flex: 1 1 auto;
    min-width: 0;
    padding: 20px 24px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(26, 31, 54, 0.06);
}

.rv-step--upcoming .rv-step__card {
    background: #F5F8FF;
    box-shadow: none;
    border: 1px dashed #B9C8EC;
}

.rv-chip {
    display: inline-block;
    margin-bottom: 8px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.5;

    &--upcoming { background: #E3EAFB; color: #154096; }
    &--current { background: #E3F5EC; color: #0B7A50; }
}

.rv-step__title {
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    color: #1A1F36;
}

.rv-step__sub {
    margin: 0 0 8px;
    font-size: 13px;
    color: #6B7490;
}

.rv-step__text {
    font-size: 15px;
    line-height: 1.6;
    color: #2E3A59;

    :deep(p) { margin: 0; }
    :deep(p + p) { margin-top: 8px; }
}

.rv-about {
    margin-top: 40px;
}

.rv-about__heading {
    margin: 0 0 14px;
    font-size: 20px;
    font-weight: 700;
    color: #1A1F36;
}

.rv-about__card {
    padding: 20px 24px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(26, 31, 54, 0.06);
}

.rv-about__card + .rv-about__card { margin-top: 12px; }

.rv-about__title {
    margin: 0 0 8px;
    font-size: 16px;
    font-weight: 700;
    color: #1A1F36;
}

.rv-about__text {
    font-size: 14px;
    line-height: 1.6;
    color: #4A5472;

    :deep(p) { margin: 0; }
    :deep(p + p) { margin-top: 8px; }
}

@media (max-width: 575px) {
    // Down to the 16px gutter phones get elsewhere, for the cards' sake
    .rv-page { padding-left: 4px; padding-right: 4px; }

    .rv-head__title { font-size: 22px; }

    .rv-step:not(:last-child)::before { left: 19px; top: 20px; }

    .rv-step { gap: 10px; }

    .rv-step__node {
        flex-basis: 40px;
        width: 40px;
        height: 40px;
        font-size: 11px;
    }

    .rv-step--current .rv-step__node { box-shadow: 0 0 0 4px rgba(21, 64, 150, 0.12); }

    .rv-step__card { padding: 16px; }

    .rv-about__card { padding: 16px; }
}
</style>
